import { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '@/contexts/Alert/AlertContext';
import { useLoading } from '@/shared/context/UIContextProvider';
import { saveUnit } from '@/data';
import { convertToUnit } from '@/services/buildingGenerator';

interface RegenerateUnitsFormProps {
  projectId: string;
  buildingId: number;
  floors: number;
  onComplete?: () => void;
}

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export function RegenerateUnitsForm({
  projectId,
  buildingId,
  floors,
  onComplete,
}: RegenerateUnitsFormProps) {
  const { showAlert } = useAlert();
  const { createLoading } = useLoading();
  const navigate = useNavigate();

  const [totalUnits, setTotalUnits] = useState<number>(20);
  const [regenerating, setRegenerating] = useState(false);

  const handleRegenerate = async () => {
    if (!totalUnits || totalUnits <= 0) {
      showAlert('Please enter a valid number of units', 'error');
      return;
    }

    setRegenerating(true);
    const loader = createLoading();

    loader.startLoading({
      title: 'Regenerating units...',
    });

    try {
      const unitsPerFloor = Math.ceil(totalUnits / floors);
      const generatedUnits = [];

      // Generate units
      let unitCount = 0;
      for (let floor = 1; floor <= floors && unitCount < totalUnits; floor++) {
        const unitsThisFloor = Math.min(unitsPerFloor, totalUnits - unitCount);

        for (let lineIndex = 0; lineIndex < unitsThisFloor; lineIndex++) {
          const line = LETTERS[lineIndex];
          const apartmentId = `${floor}${line}`;
          const meshId = `apt_${apartmentId}`;

          generatedUnits.push({
            apartmentId,
            meshId,
            floor,
            line,
            numBedrooms: 2, // Default
            numBathrooms: 1, // Default
          });

          unitCount++;
        }
      }

      // Save all units
      for (const generatedUnit of generatedUnits) {
        const unit = convertToUnit(generatedUnit, projectId, buildingId);
        await saveUnit(projectId, unit as any);
      }

      showAlert(
        `Successfully regenerated ${generatedUnits.length} units! Redirecting to rent roll...`,
        'success'
      );
      loader.stopLoading();

      // Navigate to rent roll page
      navigate(`/m/projects/${projectId}/${buildingId}/rentroll`);

      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error regenerating units:', error);
      showAlert('Failed to regenerate units', 'error');
      loader.stopLoading();
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Stack gap="20px" className="w-full">
      <Alert severity="info">
        Use this form to regenerate units if the initial creation process failed.
        This will create new unit records based on the number of floors in your building.
      </Alert>

      <Typography variant="h6" color="textPrimary">
        Regenerate Units
      </Typography>

      <Box>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          Building has {floors} floor{floors !== 1 ? 's' : ''}
        </Typography>
      </Box>

      <TextField
        label="Total Units"
        type="number"
        value={totalUnits}
        onChange={(e) => setTotalUnits(parseInt(e.target.value) || 0)}
        fullWidth
        helperText="Total number of units across all floors"
      />

      <Box>
        <Typography variant="body2" color="textSecondary">
          This will create approximately {Math.ceil(totalUnits / floors)} units per floor
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Unit naming: 1A, 1B, 1C... (Floor + Line)
        </Typography>
      </Box>

      <Button
        variant="contained"
        color="primary"
        onClick={handleRegenerate}
        disabled={regenerating || !totalUnits || totalUnits <= 0}
        fullWidth
      >
        {regenerating ? 'Regenerating Units...' : 'Regenerate Units'}
      </Button>
    </Stack>
  );
}
